import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, errText } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { LogOut, Pencil, Trash2, Plus, X } from "lucide-react";
import Logo from "@/components/Logo";
import RichEditor from "@/components/RichEditor";
import LeadsOverview from "@/components/admin/LeadsOverview";

const RESOURCES = [
  { key: "classes", label: "Classes", fields: ["slug", "name", "tagline", "description", "duration_min", "difficulty", "instructor", "equipment", "image_url"] },
  { key: "memberships", label: "Memberships", fields: ["slug", "name", "tagline", "price_gbp", "interval", "signup_url", "highlighted"] },
  { key: "blog", label: "Blog", fields: ["slug", "title", "excerpt", "body", "category", "cover_image", "author", "featured", "published"] },
  { key: "testimonials", label: "Testimonials", fields: ["name", "role", "quote", "short_quote", "rating", "image_url", "featured"] },
  { key: "faqs", label: "FAQs", fields: ["question", "answer", "category", "order"] },
];

const LEADS = [
  { key: "pipeline", label: "Leads (all funnels)", cols: ["created_at", "funnel", "first_name", "mobile", "email", "town", "goal", "preferred_contact", "status", "follow_up_at", "next_action"] },
  { key: "waitlist", label: "Women's Waitlist", cols: ["position", "name", "email", "mobile", "status", "utm_source", "created_at"] },
  { key: "consultations", label: "Consultations", cols: ["name", "email", "phone", "goals", "stage", "next_action", "created_at"] },
  { key: "contacts", label: "Contact", cols: ["name", "email", "message", "created_at"] },
  { key: "space-hire", label: "Space Hire", cols: ["name", "email", "phone", "organisation", "use_case", "preferred_times", "frequency", "created_at"] },
  { key: "newsletter", label: "Newsletter", cols: ["email", "name", "source", "created_at"] },
  { key: "scorecards", label: "Scorecard", cols: ["name", "email", "overall", "focus", "created_at"] },
  { key: "gift-vouchers", label: "Gift Vouchers", cols: ["purchaser_name", "recipient_name", "tier_label", "amount_gbp", "status", "code", "created_at"] },
];

const CONSULTATION_STAGES = [
  ["", "—"],
  ["new_enquiry", "New enquiry"],
  ["contact_attempted", "Contact attempted"],
  ["conversation_started", "Conversation started"],
  ["consultation_booked", "Consultation booked"],
  ["consultation_attended", "Consultation attended"],
  ["trial_started", "Trial started"],
  ["kore_coached_member", "Kore Coaching member"],
  ["kore_access_member", "Kore Access member"],
  ["follow_up", "Follow up"],
  ["closed_lost", "Closed / lost"],
  ["not_suitable", "Not suitable"],
];

const LEAD_STATUSES = [
  ["new", "New"], ["contacted", "Contacted"], ["booked", "Booked"], ["visited", "Visited"], ["joined", "Joined"], ["lost", "Lost"],
];

const BOOL_FIELDS = ["highlighted", "featured", "published"];
const NUM_FIELDS = ["price_gbp", "rating", "duration_min", "order"];

const parseValue = (v) => {
  if (v === "true") return true;
  if (v === "false") return false;
  if (v !== "" && !isNaN(Number(v)) && /^-?\d+(\.\d+)?$/.test(v)) return Number(v);
  return v;
};

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("classes");
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null); // {mode: 'create'|'edit', data}
  const [leadData, setLeadData] = useState({});
  const [bookings, setBookings] = useState([]);

  const current = RESOURCES.find((r) => r.key === tab);
  const isLead = LEADS.some((l) => l.key === tab);
  const isBookings = tab === "bookings";

  const load = async () => {
    if (tab === "pipeline") {
      const { data } = await api.get(`/admin/leads`);
      setLeadData({ ...leadData, pipeline: data });
    } else if (isLead) {
      const { data } = await api.get(`/admin/leads/${tab}`);
      setLeadData({ ...leadData, [tab]: data });
    } else if (isBookings) {
      const { data } = await api.get(`/admin/bookings`);
      setBookings(data);
    } else {
      const { data } = await api.get(`/admin/${tab}`);
      setItems(data);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);

  const save = async (e) => {
    e.preventDefault();
    const body = {};
    current.fields.forEach((f) => {
      const raw = editing.data[f];
      if (BOOL_FIELDS.includes(f)) {
        body[f] = raw === true || raw === "true";
      } else if (NUM_FIELDS.includes(f)) {
        const n = Number(raw);
        body[f] = Number.isFinite(n) && raw !== "" && raw !== undefined ? n : 0;
      } else {
        body[f] = raw === undefined || raw === null ? "" : parseValue(raw);
      }
    });
    try {
      if (editing.mode === "create") await api.post(`/admin/${tab}`, body);
      else await api.put(`/admin/${tab}/${editing.data.id}`, body);
      toast.success("Saved.");
      setEditing(null);
      load();
    } catch (err) { toast.error(errText(err)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this item?")) return;
    try { await api.delete(`/admin/${tab}/${id}`); toast.success("Deleted."); load(); }
    catch (err) { toast.error(errText(err)); }
  };

  const markGiftPaid = async (id) => {
    if (!window.confirm("Mark this gift voucher as paid? A voucher code will be generated and emailed to the recipient.")) return;
    try {
      const { data } = await api.post(`/admin/gift-vouchers/${id}/mark-paid`);
      toast.success(`Voucher issued: ${data.code}`);
      load();
    } catch (err) { toast.error(errText(err)); }
  };

  const redeemGift = async (id) => {
    if (!window.confirm("Mark this voucher as redeemed?")) return;
    try {
      await api.post(`/admin/gift-vouchers/${id}/redeem`);
      toast.success("Redeemed.");
      load();
    } catch (err) { toast.error(errText(err)); }
  };

  const updateConsultation = async (id, patch) => {
    try {
      await api.patch(`/admin/consultations/${id}`, patch);
      toast.success("Updated.");
      load();
    } catch (err) { toast.error(errText(err)); }
  };

  const updateLead = async (id, patch) => {
    try { await api.patch(`/admin/leads/${id}`, patch); toast.success("Updated."); load(); }
    catch (err) { toast.error(errText(err)); }
  };

  const exportConsultations = async () => {
    try {
      const { data } = await api.get(`/admin/consultations.csv`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `kore-consultations-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) { toast.error(errText(err)); }
  };

  return (
    <div className="min-h-screen flex bg-kore-warm">
      <aside className="w-64 bg-kore-forest text-kore-warm min-h-screen py-8 px-6 sticky top-0 h-screen">
        <div className="text-kore-light">
          <Logo size="md" asLink={false} />
        </div>
        <div className="mt-2 text-xs text-kore-warm/50">{user?.email}</div>
        <nav className="mt-10 space-y-1">
          <div className="eyebrow text-kore-light mb-3">Content</div>
          {RESOURCES.map((r) => (
            <button key={r.key} onClick={() => setTab(r.key)} data-testid={`admin-tab-${r.key}`} className={`block w-full text-left px-3 py-2 rounded-lg text-sm ${tab === r.key ? "bg-kore-light/15 text-kore-light" : "text-kore-warm/70 hover:text-kore-warm"}`}>
              {r.label}
            </button>
          ))}
          <div className="eyebrow text-kore-light mt-8 mb-3">Leads</div>
          {LEADS.map((r) => (
            <button key={r.key} onClick={() => setTab(r.key)} data-testid={`admin-tab-${r.key}`} className={`block w-full text-left px-3 py-2 rounded-lg text-sm ${tab === r.key ? "bg-kore-light/15 text-kore-light" : "text-kore-warm/70 hover:text-kore-warm"}`}>
              {r.label}
            </button>
          ))}
          <button onClick={() => setTab("bookings")} data-testid="admin-tab-bookings" className={`block w-full text-left px-3 py-2 rounded-lg text-sm ${tab === "bookings" ? "bg-kore-light/15 text-kore-light" : "text-kore-warm/70 hover:text-kore-warm"}`}>Bookings</button>
        </nav>
        <div className="absolute bottom-8 left-6 right-6 space-y-3">
          <Link to="/" className="block text-xs text-kore-warm/60 hover:text-kore-light">← View site</Link>
          <button onClick={logout} className="flex items-center gap-2 text-xs text-kore-warm/60 hover:text-kore-light"><LogOut size={12} /> Sign out</button>
        </div>
      </aside>

      <main className="flex-1 p-10 max-w-6xl">
        <div className="flex items-center justify-between mb-8">
          <h1 className="font-display text-4xl text-kore-forest">{current?.label || (LEADS.find(l => l.key === tab)?.label) || "Bookings"}</h1>
          {!isLead && !isBookings && (
            <button onClick={() => setEditing({ mode: "create", data: {} })} data-testid="admin-new" className="btn-primary !py-2.5 !px-5 text-xs"><Plus size={14} /> New</button>
          )}
          {tab === "consultations" && (
            <button onClick={exportConsultations} data-testid="consultations-export-csv" className="btn-ghost !py-2.5 !px-5 text-xs">Export CSV</button>
          )}
        </div>

        <LeadsOverview key={tab} onOpenLead={() => setTab("pipeline")} />

        {isLead ? (
          <div className="bg-white rounded-2xl border border-kore-gold/20 overflow-x-auto">
            <table className="w-full text-sm min-w-[960px]">
              <thead className="bg-kore-warm text-left text-[11px] uppercase tracking-[0.2em] text-kore-gold">
                <tr>
                  {LEADS.find(l => l.key === tab).cols.map((c) => <th key={c} className="px-4 py-3">{c.replace(/_/g, " ")}</th>)}
                  {tab === "gift-vouchers" && <th className="px-4 py-3">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {(leadData[tab] || []).map((row) => (
                  <tr key={row.id} className="border-t border-kore-gold/10">
                    {LEADS.find(l => l.key === tab).cols.map((c) => (
                      <td key={c} className="px-4 py-3 text-kore-ink/80">
                        {c === "amount_gbp" && row[c] != null
                          ? `£${Number(row[c]).toFixed(2)}`
                          : c === "status" && tab !== "pipeline"
                            ? <span className={`text-[11px] uppercase tracking-[0.16em] ${row[c] === "paid" ? "text-kore-forest" : row[c] === "redeemed" ? "text-kore-gold" : "text-kore-ink/60"}`}>{row[c] || "—"}</span>
                            : tab === "pipeline" && c === "status"
                              ? (
                                <select data-testid={`lead-status-${row.id}`} value={row.status || "new"} onChange={(e) => updateLead(row.id, { status: e.target.value })} className="text-xs border border-kore-gold/30 rounded-md px-2 py-1 bg-white focus:outline-none focus:border-kore-forest">
                                  {LEAD_STATUSES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                                </select>
                              )
                            : tab === "pipeline" && c === "follow_up_at"
                              ? <input type="date" data-testid={`lead-followup-${row.id}`} defaultValue={row.follow_up_at || ""} onBlur={(e) => e.target.value !== (row.follow_up_at || "") && updateLead(row.id, { follow_up_at: e.target.value })} className="text-xs border-b border-kore-gold/20 bg-transparent py-1 focus:outline-none focus:border-kore-forest" />
                            : tab === "pipeline" && c === "next_action"
                              ? <input data-testid={`lead-next-${row.id}`} defaultValue={row.next_action || ""} onBlur={(e) => e.target.value !== (row.next_action || "") && updateLead(row.id, { next_action: e.target.value })} placeholder="Add next action…" className="text-xs border-b border-kore-gold/20 focus:border-kore-forest focus:outline-none bg-transparent w-full py-1" />
                            : tab === "pipeline" && c === "created_at"
                              ? String(row[c] || "").slice(0, 10)
                            : c === "stage" && tab === "consultations"
                              ? (
                                <select
                                  data-testid={`stage-select-${row.id}`}
                                  value={row.stage || ""}
                                  onChange={(e) => updateConsultation(row.id, { stage: e.target.value })}
                                  className="text-xs border border-kore-gold/30 rounded-md px-2 py-1 bg-white focus:outline-none focus:border-kore-forest"
                                >
                                  {CONSULTATION_STAGES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                                </select>
                              )
                              : c === "next_action" && tab === "consultations"
                                ? (
                                  <input
                                    data-testid={`next-action-${row.id}`}
                                    defaultValue={row.next_action || ""}
                                    onBlur={(e) => e.target.value !== (row.next_action || "") && updateConsultation(row.id, { next_action: e.target.value })}
                                    placeholder="Add next action…"
                                    className="text-xs border-b border-kore-gold/20 focus:border-kore-forest focus:outline-none bg-transparent w-full py-1"
                                  />
                                )
                                : String(row[c] || "—").slice(0, 80)}
                      </td>
                    ))}
                    {tab === "gift-vouchers" && (
                      <td className="px-4 py-3 flex gap-2">
                        {row.status === "pending_payment" && (
                          <button onClick={() => markGiftPaid(row.id)} data-testid={`gift-mark-paid-${row.id}`} className="text-[11px] uppercase tracking-[0.16em] text-kore-forest border border-kore-forest rounded-full px-3 py-1 hover:bg-kore-forest hover:text-kore-warm transition">Mark paid</button>
                        )}
                        {row.status === "paid" && (
                          <button onClick={() => redeemGift(row.id)} data-testid={`gift-redeem-${row.id}`} className="text-[11px] uppercase tracking-[0.16em] text-kore-gold border border-kore-gold rounded-full px-3 py-1 hover:bg-kore-gold hover:text-kore-warm transition">Redeem</button>
                        )}
                        {row.status === "redeemed" && <span className="text-[11px] uppercase tracking-[0.16em] text-kore-ink/40">Complete</span>}
                      </td>
                    )}
                  </tr>
                ))}
                {(!leadData[tab] || leadData[tab].length === 0) && <tr><td colSpan={8} className="px-4 py-10 text-center text-kore-ink/50">No leads yet.</td></tr>}
              </tbody>
            </table>
          </div>
        ) : isBookings ? (
          <div className="bg-white rounded-2xl border border-kore-gold/20 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-kore-warm text-left text-[11px] uppercase tracking-[0.2em] text-kore-gold">
                <tr><th className="px-4 py-3">Class</th><th className="px-4 py-3">Customer</th><th className="px-4 py-3">When</th><th className="px-4 py-3">Amount</th><th className="px-4 py-3">Status</th></tr>
              </thead>
              <tbody>
                {bookings.map((b) => (
                  <tr key={b.id} className="border-t border-kore-gold/10">
                    <td className="px-4 py-3 text-kore-ink/80">{b.class_name}</td>
                    <td className="px-4 py-3 text-kore-ink/80">{b.customer_name}<br/><span className="text-xs text-kore-ink/50">{b.customer_email}</span></td>
                    <td className="px-4 py-3 text-kore-ink/80">{b.session_datetime || "—"}</td>
                    <td className="px-4 py-3 text-kore-ink/80">£{b.amount_gbp?.toFixed(2)}</td>
                    <td className="px-4 py-3"><span className={`text-[11px] uppercase tracking-[0.16em] ${b.payment_status === "paid" ? "text-kore-forest" : "text-kore-gold"}`}>{b.payment_status}</span></td>
                  </tr>
                ))}
                {bookings.length === 0 && <tr><td colSpan={5} className="px-4 py-10 text-center text-kore-ink/50">No bookings yet.</td></tr>}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="grid gap-3">
            {items.map((it) => (
              <div key={it.id} data-testid={`admin-row-${tab}-${it.id}`} className="bg-white rounded-2xl border border-kore-gold/15 px-6 py-4 flex items-center justify-between">
                <div>
                  <div className="font-medium text-kore-forest">{it.name || it.title || it.question || it.quote?.slice(0, 60)}</div>
                  <div className="text-xs text-kore-ink/50 mt-1">{it.slug || it.category || it.role || it.email || it.id}</div>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => setEditing({ mode: "edit", data: it })} data-testid={`admin-edit-${it.id}`} className="p-2 text-kore-ink/60 hover:text-kore-gold"><Pencil size={15} /></button>
                  <button onClick={() => remove(it.id)} data-testid={`admin-delete-${it.id}`} className="p-2 text-kore-ink/60 hover:text-red-500"><Trash2 size={15} /></button>
                </div>
              </div>
            ))}
            {items.length === 0 && <p className="text-kore-ink/50 text-sm">Nothing yet — click New to add one.</p>}
          </div>
        )}

        {editing && !isLead && !isBookings && (
          <div className="fixed inset-0 bg-kore-forest/40 backdrop-blur-sm z-50 flex items-center justify-center p-6 overflow-y-auto">
            <form onSubmit={save} className="bg-white rounded-3xl w-full max-w-2xl p-8 my-8 max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-6">
                <h2 className="font-display text-2xl text-kore-forest">{editing.mode === "create" ? "Create" : "Edit"}</h2>
                <button type="button" onClick={() => setEditing(null)} className="text-kore-ink/60"><X size={18} /></button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {current.fields.map((f) => {
                  const isLong = ["description", "quote", "short_quote", "answer", "excerpt"].includes(f);
                  const isRich = f === "body";
                  const isBool = ["highlighted", "featured", "published"].includes(f);
                  const val = editing.data[f] ?? "";
                  return (
                    <div key={f} className={(isLong || isRich) ? "md:col-span-2" : ""}>
                      <label className="eyebrow">{f.replace(/_/g, " ")}</label>
                      {isBool ? (
                        <select value={String(val)} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, [f]: e.target.value } })} className="mt-2 w-full border-b border-kore-ink/20 py-2 bg-transparent focus:outline-none focus:border-kore-gold">
                          <option value="false">False</option><option value="true">True</option>
                        </select>
                      ) : isRich ? (
                        <div className="mt-2">
                          <RichEditor value={val} onChange={(html) => setEditing({ ...editing, data: { ...editing.data, [f]: html } })} />
                        </div>
                      ) : isLong ? (
                        <textarea rows={5} value={val} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, [f]: e.target.value } })} className="mt-2 w-full border border-kore-ink/15 rounded-lg p-3 bg-transparent focus:outline-none focus:border-kore-gold resize-none text-sm" />
                      ) : (
                        <input value={val} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, [f]: e.target.value } })} className="mt-2 w-full border-b border-kore-ink/20 py-2 bg-transparent focus:outline-none focus:border-kore-gold" />
                      )}
                    </div>
                  );
                })}
              </div>
              <div className="mt-8 flex gap-3">
                <button type="submit" className="btn-primary" data-testid="admin-save">Save</button>
                <button type="button" onClick={() => setEditing(null)} className="btn-ghost">Cancel</button>
              </div>
            </form>
          </div>
        )}
      </main>
    </div>
  );
}
